<template>
    <PageHeader :title="headerTitle" back-to="/stock"/>

    <div v-if="stock && !isCreate" class="stk-header-card">
        <div class="stk-header-main">
            <div class="stk-header-text">
                <span class="stk-header-eyebrow">{{ stock.category?.name || '-' }}</span>
                <span class="stk-header-name">{{ stock.name }}</span>
                <div class="stk-header-meta">
                    <span class="stk-meta-pill" v-if="stock.code">
                        <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><rect x="3" y="3" width="18" height="18" rx="2"/><path d="M9 9h6v6H9z"/></svg>
                        {{ stock.code }}
                    </span>
                    <span class="stk-meta-pill" v-if="stock.unit_type">{{ stock.unit_type.name }}</span>
                    <span class="stk-meta-pill" v-if="!stock.active">{{ $t('common.passive') }}</span>
                </div>
            </div>
            <div class="stk-header-stats">
                <div class="stk-stat">
                    <span class="stk-stat-label">{{ $t('pages.stock.cols.total_quantity') }}</span>
                    <span class="stk-stat-value" :class="lowStock ? 'stk-stat-value--warn' : ''">
                        {{ formatQty(stock.total_quantity) }}
                    </span>
                    <span v-if="lowStock" class="stk-stat-badge">{{ $t('pages.stock.lowStockBadge') }}</span>
                </div>
                <div class="stk-stat">
                    <span class="stk-stat-label">{{ $t('pages.stock.cols.sale_price') }}</span>
                    <span class="stk-stat-value">{{ formatTRY(stock.sale_price) }}</span>
                </div>
            </div>
        </div>
    </div>

    <div v-if="!isCreate" class="stk-tabs">
        <button class="stk-tab" :class="{'stk-tab--active': activeTab === 'info'}" @click="activeTab = 'info'">
            {{ $t('pages.stock.detail.infoTab') }}
        </button>
        <button class="stk-tab" :class="{'stk-tab--active': activeTab === 'locations'}" @click="activeTab = 'locations'">
            {{ $t('pages.stock.detail.locationsTab') }}
            <span class="stk-tab-count">{{ (stock?.locations || []).length }}</span>
        </button>
        <button class="stk-tab" :class="{'stk-tab--active': activeTab === 'movements'}" @click="activeTab = 'movements'">
            {{ $t('pages.stock.detail.movementsTab') }}
            <span class="stk-tab-count">{{ movementTable.pagination?.total || 0 }}</span>
        </button>
    </div>

    <!-- INFO TAB -->
    <div v-show="activeTab === 'info'" class="stk-section">
        <StockInfoForm
            :stock="stock"
            :is-create="isCreate"
            @saved="onInfoSaved"/>
    </div>

    <!-- LOCATIONS TAB -->
    <div v-show="activeTab === 'locations'" class="stk-section">
        <div class="stk-section-head">
            <span class="stk-section-title">{{ $t('pages.stock.location.title') }}</span>
            <button class="stk-btn stk-btn--primary stk-btn--sm" @click="openLocationDialog">
                <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.6">
                    <line x1="12" y1="5" x2="12" y2="19"/>
                    <line x1="5" y1="12" x2="19" y2="12"/>
                </svg>
                {{ $t('pages.stock.location.add') }}
            </button>
        </div>

        <div v-if="!stock || !stock.locations || stock.locations.length === 0" class="stk-empty">
            <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.6">
                <path d="M21 8V21H3V8"/>
                <rect x="1" y="3" width="22" height="5" rx="1"/>
                <line x1="10" y1="12" x2="14" y2="12"/>
            </svg>
            <span class="stk-empty-title">{{ $t('pages.stock.detail.noLocationsHint') }}</span>
            <span class="stk-empty-hint">{{ $t('pages.stock.location.emptyHint') }}</span>
        </div>

        <div v-else class="stk-loc-grid">
            <div v-for="loc in (stock.locations || [])" :key="loc.id" class="stk-loc-card">
                <div class="stk-loc-head">
                    <div class="stk-loc-name">
                        <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8">
                            <path d="M21 8V21H3V8"/>
                            <rect x="1" y="3" width="22" height="5" rx="1"/>
                        </svg>
                        {{ loc.warehouse?.name }}
                        <span v-if="loc.warehouse?.is_default" class="stk-loc-default-pill">
                            {{ $t('pages.cash_register.defaultBadge') }}
                        </span>
                    </div>
                    <button class="stk-loc-del" :title="$t('pages.stock.location.delete')" @click="onDeleteLocation(loc)">
                        <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                            <polyline points="3 6 5 6 21 6"/>
                            <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6"/>
                        </svg>
                    </button>
                </div>
                <div class="stk-loc-qty">
                    <span class="stk-loc-qty-label">{{ $t('pages.stock.location.currentQuantity') }}</span>
                    <span class="stk-loc-qty-value" :class="parseFloat(loc.current_quantity) <= 0 ? 'stk-loc-qty-value--zero' : ''">
                        {{ formatQty(loc.current_quantity) }}
                    </span>
                </div>
                <div class="stk-loc-actions">
                    <button class="stk-loc-act stk-loc-act--in" @click="openLocationMovement(loc, 1)">
                        <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.6" stroke-linecap="round" stroke-linejoin="round">
                            <line x1="12" y1="19" x2="12" y2="5"/>
                            <polyline points="5 12 12 5 19 12"/>
                        </svg>
                        {{ $t('pages.stock.location.quickAdd') }}
                    </button>
                    <button class="stk-loc-act stk-loc-act--out" @click="openLocationMovement(loc, 2)">
                        <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.6" stroke-linecap="round" stroke-linejoin="round">
                            <line x1="12" y1="5" x2="12" y2="19"/>
                            <polyline points="19 12 12 19 5 12"/>
                        </svg>
                        {{ $t('pages.stock.location.quickRemove') }}
                    </button>
                </div>
            </div>
        </div>
    </div>

    <!-- MOVEMENTS TAB -->
    <div v-show="activeTab === 'movements'" class="stk-section">
        <div class="stk-section-head">
            <span class="stk-section-title">{{ $t('pages.stock.movement.title') }}</span>
            <div class="stk-section-actions">
                <button class="stk-btn stk-btn--sm" @click="reloadMovements">
                    <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.4">
                        <polyline points="23 4 23 10 17 10"/>
                        <path d="M20.49 15a9 9 0 1 1-2.12-9.36L23 10"/>
                    </svg>
                    {{ $t('btn.refresh') }}
                </button>
                <button class="stk-btn stk-btn--sm" :disabled="!hasLocations" @click="openTransferDialog">
                    <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round">
                        <polyline points="16 3 21 3 21 8"/>
                        <line x1="4" y1="20" x2="21" y2="3"/>
                        <polyline points="21 16 21 21 16 21"/>
                        <line x1="15" y1="15" x2="21" y2="21"/>
                        <line x1="4" y1="4" x2="9" y2="9"/>
                    </svg>
                    {{ $t('pages.stock.movement.transfer') }}
                </button>
                <button class="stk-btn stk-btn--primary stk-btn--sm" :disabled="!hasLocations" @click="openMovementDialog">
                    <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.6">
                        <line x1="12" y1="5" x2="12" y2="19"/>
                        <line x1="5" y1="12" x2="19" y2="12"/>
                    </svg>
                    {{ $t('pages.stock.movement.add') }}
                </button>
            </div>
        </div>

        <div class="stk-mvm-filter">
            <div class="stk-mvm-filter-field">
                <label class="stk-mvm-filter-label">{{ $t('pages.stock.movement.cols.type') }}</label>
                <el-select v-model="mvmFilter.movement_type" class="w-100" clearable
                    :placeholder="$t('common.viewAll')" @change="applyMovementFilter">
                    <el-option :value="1" :label="$t('pages.stock.movement.typeLabel.1')"/>
                    <el-option :value="2" :label="$t('pages.stock.movement.typeLabel.2')"/>
                    <el-option :value="3" :label="$t('pages.stock.movement.typeLabel.3')"/>
                </el-select>
            </div>
            <div class="stk-mvm-filter-field">
                <label class="stk-mvm-filter-label">{{ $t('pages.stock.movement.cols.location') }}</label>
                <el-select v-model="mvmFilter.location_id" class="w-100" clearable filterable
                    :placeholder="$t('common.viewAll')" @change="applyMovementFilter">
                    <el-option v-for="loc in (stock?.locations || [])" :key="loc.id"
                        :value="loc.id" :label="loc.warehouse?.name || ('#' + loc.id)"/>
                </el-select>
            </div>
            <div class="stk-mvm-filter-field">
                <label class="stk-mvm-filter-label">{{ $t('filters.transaction.createdAtMin') }}</label>
                <DateTimePicker v-model="mvmFilter.createdAtMin" type="date" clearable auto-modal
                    :max="mvmFilter.createdAtMax || null"
                    :placeholder="$t('common.chooseDate')" @change="applyMovementFilter"/>
            </div>
            <div class="stk-mvm-filter-field">
                <label class="stk-mvm-filter-label">{{ $t('filters.transaction.createdAtMax') }}</label>
                <DateTimePicker v-model="mvmFilter.createdAtMax" type="date" clearable auto-modal
                    :min="mvmFilter.createdAtMin || null"
                    :placeholder="$t('common.chooseDate')" @change="applyMovementFilter"/>
            </div>
            <button class="stk-btn stk-btn--sm stk-mvm-filter-reset" @click="resetMovementFilter">
                {{ $t('btn.reset') }}
            </button>
        </div>

        <custom-table
            hide-header="true"
            rowKey="id"
            :items="movementTable.data"
            :loading="movementTable.loading"
            :columns="movementFields"
            :pagination="movementTable.pagination"
            :sortMeta="movementTable.pagination.sort || []"
            changeUrlPage
            :emptyTableText="$t('pages.stock.detail.noMovementsHint')"
            @changeTable="handleMovementTableChange"
            @sort="handleMovementSort"
        >
            <template v-slot:type="{ row: record }">
                <span class="stk-mvm-type" :class="movementTypeClass(record.movement_type)">
                    <svg v-if="record.movement_type === 1" width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="round" stroke-linejoin="round">
                        <line x1="12" y1="19" x2="12" y2="5"/>
                        <polyline points="5 12 12 5 19 12"/>
                    </svg>
                    <svg v-else-if="record.movement_type === 2" width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="round" stroke-linejoin="round">
                        <line x1="12" y1="5" x2="12" y2="19"/>
                        <polyline points="19 12 12 19 5 12"/>
                    </svg>
                    <svg v-else width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round">
                        <path d="M3 6h18M3 12h18M3 18h18"/>
                    </svg>
                    {{ $t('pages.stock.movement.typeLabel.' + record.movement_type) }}
                </span>
            </template>

            <template v-slot:location="{ row: record }">
                <span class="stk-mvm-loc">{{ record.location?.warehouse?.name || '-' }}</span>
            </template>

            <template v-slot:quantity="{ row: record }">
                <span class="stk-mvm-qty" :class="movementQtyClass(record.movement_type)">
                    {{ qtyPrefix(record.movement_type) }} {{ formatQty(record.quantity) }}
                </span>
            </template>

            <template v-slot:unit_price="{ row: record }">
                <span class="stk-mvm-price">{{ formatTRY(record.unit_price) }}</span>
                <el-tooltip v-if="record.is_expense" :content="$t('pages.stock.location.expenseTooltip')" placement="top">
                    <span class="stk-mvm-expense-pill">
                        <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round">
                            <rect x="2" y="6" width="20" height="13" rx="2"/>
                            <line x1="2" y1="11" x2="22" y2="11"/>
                            <line x1="6" y1="15" x2="9" y2="15"/>
                        </svg>
                    </span>
                </el-tooltip>
            </template>

            <template v-slot:note="{ row: record }">
                <span v-if="record.note" class="stk-mvm-note">{{ record.note }}</span>
                <span v-else-if="record.source_type" class="stk-mvm-source">{{ sourceLabel(record.source_type) }} #{{ record.source_id }}</span>
                <span v-else class="stk-mvm-muted">—</span>
            </template>

            <template v-slot:created_at="{ row: record }">
                {{ formatDate(record.created_at) }}
            </template>

            <template v-slot:actions="{ row: record }">
                <div class="d-flex justify-content-end">
                    <el-tooltip v-if="record.movement_type !== 3" :content="$t('common.delete')" placement="top">
                        <a class="btn btn-icon btn-bg-light btn-active-color-primary btn-sm" @click="confirmDeleteMovement(record)">
                            <span class="svg-icon svg-icon-3">
                                <inline-svg src="/media/icons/duotune/general/gen027.svg"/>
                            </span>
                        </a>
                    </el-tooltip>
                    <el-tooltip v-else :content="$t('pages.stock.movement.deleteAdjustment')" placement="top">
                        <a class="btn btn-icon btn-bg-light btn-sm stk-action--disabled">
                            <span class="svg-icon svg-icon-3">
                                <inline-svg src="/media/icons/duotune/general/gen027.svg"/>
                            </span>
                        </a>
                    </el-tooltip>
                </div>
            </template>
        </custom-table>
    </div>

    <StockLocationDialog ref="locationDialog"
        :stock-id="stockId"
        :existing-locations="stock?.locations || []"
        @created="onLocationChanged"/>

    <StockMovementDialog ref="movementDialog"
        :stock-id="stockId"
        :locations="stock?.locations || []"
        @created="onMovementChanged"/>

    <StockTransferDialog ref="transferDialog"
        :stock-id="stockId"
        :locations="stock?.locations || []"
        @created="onMovementChanged"/>
</template>

<script>
import CustomTable from "@/components/custom-table";
import PageHeader from "@/components/page-header/PageHeader.vue";
import DateTimePicker from "@/components/common/date-time-picker/DateTimePicker.vue";
import StockInfoForm from "@/components/stock/StockInfoForm.vue";
import StockLocationDialog from "@/components/stock/StockLocationDialog.vue";
import StockMovementDialog from "@/components/stock/StockMovementDialog.vue";
import StockTransferDialog from "@/components/stock/StockTransferDialog.vue";

export default {
    name: "stock-detail",
    components: { CustomTable, PageHeader, DateTimePicker, StockInfoForm, StockLocationDialog, StockMovementDialog, StockTransferDialog },
    data() {
        return {
            activeTab: 'info',
            stock: null,
            mvmFilter: {
                movement_type: null,
                location_id: null,
                createdAtMin: '',
                createdAtMax: '',
            },
            movementFilterParams: { sort: 'created_at:desc' },
            movementFields: [
                { name: this.$t("pages.stock.movement.cols.date"), sortingField: "created_at", sortable: true, scopedSlots: { customRender: "created_at" } },
                { name: this.$t("pages.stock.movement.cols.type"), sortingField: "movement_type", sortable: true, scopedSlots: { customRender: "type" } },
                { name: this.$t("pages.stock.movement.cols.location"), scopedSlots: { customRender: "location" } },
                { name: this.$t("pages.stock.movement.cols.quantity"), sortingField: "quantity", sortable: true, scopedSlots: { customRender: "quantity" } },
                { name: this.$t("pages.stock.movement.cols.unitPrice"), sortingField: "unit_price", sortable: true, scopedSlots: { customRender: "unit_price" } },
                { name: this.$t("pages.stock.movement.cols.note"), scopedSlots: { customRender: "note" } },
                { name: this.$t("common.action"), key: "action", scopedSlots: { customRender: "actions" } },
            ],
        };
    },
    computed: {
        stockId() {
            return this.$route.params.id;
        },
        isCreate() {
            return this.stockId === 'new';
        },
        headerTitle() {
            if (this.isCreate) return this.$t('pages.stock.newStock');
            if (this.stock) {
                return this.sprintf(this.$t("pages.stock.detailStock"), [this.stock.name]);
            }
            return this.$t('pages.stock.detail.title');
        },
        lowStock() {
            const min = parseFloat(this.stock?.min_quantity);
            const cur = parseFloat(this.stock?.total_quantity);
            if (isNaN(min) || isNaN(cur)) return false;
            if (min <= 0) return false;
            return cur < min;
        },
        hasLocations() {
            return (this.stock?.locations || []).length > 0;
        },
        movementTable() {
            return this.$store.state.stock.movement.table;
        },
    },
    mounted() {
        this.setCurrentPageBreadcrumbs(this.$t('pages.stock.detail.title'), []);
        if (this.isCreate) {
            this.activeTab = 'info';
        } else {
            this.loadStock();
            this.refreshMovements();
        }
    },
    methods: {
        formatTRY(amount) {
            const n = parseFloat(amount);
            if (isNaN(n)) return '-';
            return n.toLocaleString('tr-TR', { style: 'currency', currency: 'TRY', minimumFractionDigits: 2 });
        },
        formatQty(q) {
            const n = parseFloat(q);
            if (isNaN(n)) return '0,00';
            return n.toLocaleString('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
        },
        formatDate(d) {
            if (!d) return '-';
            const dt = new Date(d);
            if (isNaN(dt.getTime())) return d;
            return dt.toLocaleDateString('tr-TR', { day: '2-digit', month: '2-digit', year: 'numeric' })
                + ' · ' + dt.toLocaleTimeString('tr-TR', { hour: '2-digit', minute: '2-digit' });
        },
        movementTypeClass(t) {
            if (t === 1) return 'stk-mvm-type--in';
            if (t === 2) return 'stk-mvm-type--out';
            if (t === 3) return 'stk-mvm-type--adj';
            return '';
        },
        movementQtyClass(t) {
            if (t === 1) return 'stk-mvm-qty--in';
            if (t === 2) return 'stk-mvm-qty--out';
            return 'stk-mvm-qty--adj';
        },
        qtyPrefix(t) {
            if (t === 1) return '+';
            if (t === 2) return '−';
            return '=';
        },
        sourceLabel(type) {
            const key = 'pages.stock.movement.source.' + type;
            const t = this.$t(key);
            return t === key ? type : t;
        },
        async loadStock() {
            await this.$store.dispatch('stock/show', { id: this.stockId });
            this.stock = this.$store.state.stock.save?.data || null;
        },
        buildMovementFilter() {
            const f = { sort: this.movementFilterParams.sort || 'created_at:desc' };
            if (this.mvmFilter.movement_type) f.movement_type = this.mvmFilter.movement_type;
            if (this.mvmFilter.location_id) f.location_id = this.mvmFilter.location_id;
            if (this.mvmFilter.createdAtMin) f['created_at[min]'] = String(this.mvmFilter.createdAtMin).slice(0, 10);
            if (this.mvmFilter.createdAtMax) f['created_at[max]'] = String(this.mvmFilter.createdAtMax).slice(0, 10);
            return f;
        },
        refreshMovements(pagination) {
            this.movementFilterParams = this.buildMovementFilter();
            this.$store.dispatch('stock/movement/get', {
                stockId: this.stockId,
                page: pagination,
                filterData: this.movementFilterParams,
            });
        },
        reloadMovements() {
            this.refreshMovements(this.movementTable.pagination);
        },
        applyMovementFilter() {
            this.refreshMovements();
        },
        resetMovementFilter() {
            this.mvmFilter = { movement_type: null, location_id: null, createdAtMin: '', createdAtMax: '' };
            this.movementFilterParams = { sort: 'created_at:desc' };
            this.refreshMovements();
        },
        handleMovementTableChange(pagination, filterData) {
            const currentSort = this.movementFilterParams.sort || 'created_at:desc';
            this.movementFilterParams = { ...this.buildMovementFilter(), ...filterData, sort: currentSort };
            this.$store.dispatch('stock/movement/get', {
                stockId: this.stockId,
                page: pagination,
                filterData: this.movementFilterParams,
            });
        },
        handleMovementSort({ columnName, order }) {
            this.movementFilterParams = {
                ...this.buildMovementFilter(),
                sort: `${columnName}:${order}`,
            };
            this.$store.dispatch('stock/movement/get', {
                stockId: this.stockId,
                page: this.movementTable.pagination,
                filterData: this.movementFilterParams,
            });
        },
        openLocationDialog() { this.$refs.locationDialog?.open(); },
        openMovementDialog() { this.$refs.movementDialog?.open(); },
        openLocationMovement(loc, type) {
            this.$refs.movementDialog?.open({
                movement_type: type,
                stock_location_id: loc.id,
            });
        },
        openTransferDialog() { this.$refs.transferDialog?.open(); },
        async onDeleteLocation(loc) {
            const qty = parseFloat(loc.current_quantity) || 0;
            this.eventBus.$emit('showConfirmDialog', {
                title: this.$t('pages.stock.location.delete'),
                message: qty > 0
                    ? this.$t('pages.stock.errors.cannot_delete_location_with_stock')
                    : this.$t('pages.stock.location.confirmDelete'),
                confirmText: this.$t('common.delete'),
                confirmType: 'danger',
                onConfirm: async () => {
                    const res = await this.$store.dispatch('stock/location/delete', {
                        stockId: this.stockId,
                        locationId: loc.id,
                    });
                    if (res?.status) {
                        this.$message.success(this.$t('messages.deleteOk'));
                        this.loadStock();
                    }
                },
            });
        },
        confirmDeleteMovement(record) {
            this.eventBus.$emit('showConfirmDialog', {
                title: this.$t('common.delete'),
                message: this.$t('pages.stock.movement.deleteHint'),
                confirmText: this.$t('common.delete'),
                confirmType: 'danger',
                onConfirm: () => this.onDeleteMovement(record),
            });
        },
        async onDeleteMovement(record) {
            await this.$store.dispatch('stock/movement/delete', {
                stockId: this.stockId,
                id: record.id,
            });
            this.loadStock();
            this.refreshMovements(this.movementTable.pagination);
        },
        onLocationChanged() {
            this.loadStock();
        },
        onMovementChanged() {
            this.loadStock();
            this.refreshMovements();
        },
        onInfoSaved({ id, isCreate }) {
            if (isCreate && id) {
                this.$router.replace('/stock/detail/' + id);
            } else {
                this.loadStock();
            }
        },
    },
};
</script>

<style scoped>
/* HEADER CARD */
.stk-header-card {
    margin: 12px 0 18px;
    padding: 20px 24px;
    border-radius: 14px;
    background: #fff;
    border: 1px solid rgba(0, 0, 0, 0.06);
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.04), 0 8px 24px rgba(0, 0, 0, 0.04);
    display: flex;
    flex-direction: column;
    gap: 14px;
}

.stk-header-main {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 20px;
    flex-wrap: wrap;
}

.stk-header-text {
    display: flex;
    flex-direction: column;
    gap: 4px;
    min-width: 0;
}

.stk-header-eyebrow {
    font-size: 11px;
    font-weight: 700;
    color: #8e8e93;
    text-transform: uppercase;
    letter-spacing: 0.6px;
}

.stk-header-name {
    font-size: 20px;
    font-weight: 700;
    color: #1c1c1e;
    letter-spacing: -0.2px;
}

.stk-header-meta {
    display: flex;
    gap: 6px;
    flex-wrap: wrap;
    margin-top: 4px;
}

.stk-meta-pill {
    display: inline-flex;
    align-items: center;
    gap: 4px;
    padding: 2px 9px;
    border-radius: 999px;
    background: rgba(0, 0, 0, 0.04);
    color: #6b7280;
    font-size: 11px;
    font-weight: 600;
}

.stk-header-stats {
    display: flex;
    gap: 18px;
    align-items: flex-start;
}

.stk-stat {
    display: flex;
    flex-direction: column;
    gap: 2px;
    align-items: flex-end;
    min-width: 100px;
}

.stk-stat-label {
    font-size: 10.5px;
    font-weight: 600;
    color: #8e8e93;
    text-transform: uppercase;
    letter-spacing: 0.4px;
}

.stk-stat-value {
    font-size: 19px;
    font-weight: 700;
    color: #1c1c1e;
    font-variant-numeric: tabular-nums;
    line-height: 1.2;
}

.stk-stat-value--warn { color: #d97706; }

.stk-stat-badge {
    margin-top: 2px;
    font-size: 10px;
    font-weight: 700;
    color: #d97706;
    text-transform: uppercase;
    letter-spacing: 0.3px;
}

.stk-header-actions {
    display: flex;
    gap: 8px;
    border-top: 1px solid rgba(0, 0, 0, 0.05);
    padding-top: 14px;
}

html.dark .stk-header-card {
    background: #15171C;
    border-color: rgba(255, 255, 255, 0.07);
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.3), 0 8px 24px rgba(0, 0, 0, 0.25);
}

html.midnight .stk-header-card {
    background: #1A1714;
    border-color: rgba(255, 255, 255, 0.07);
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.3), 0 8px 24px rgba(0, 0, 0, 0.25);
}

html.cream .stk-header-card {
    background: #FEFCF8;
    border-color: rgba(61, 56, 50, 0.1);
}

html.dark .stk-header-eyebrow,
html.midnight .stk-header-eyebrow,
html.dark .stk-stat-label,
html.midnight .stk-stat-label { color: #9A9CAE; }

html.cream .stk-header-eyebrow,
html.cream .stk-stat-label { color: #847C72; }

html.dark .stk-header-name,
html.midnight .stk-header-name,
html.dark .stk-stat-value,
html.midnight .stk-stat-value { color: #EAE6E1; }

html.cream .stk-header-name,
html.cream .stk-stat-value { color: #3D3832; }

html.dark .stk-meta-pill,
html.midnight .stk-meta-pill {
    background: rgba(255, 255, 255, 0.06);
    color: #9A9CAE;
}

html.cream .stk-meta-pill {
    background: rgba(61, 56, 50, 0.06);
    color: #847C72;
}

html.dark .stk-header-actions,
html.midnight .stk-header-actions { border-top-color: rgba(255, 255, 255, 0.07); }

html.cream .stk-header-actions { border-top-color: rgba(61, 56, 50, 0.08); }


/* TABS */
.stk-tabs {
    display: flex;
    gap: 4px;
    border-bottom: 1px solid rgba(0, 0, 0, 0.08);
    margin-bottom: 18px;
}

.stk-tab {
    border: none;
    background: transparent;
    padding: 10px 16px;
    font-size: 13px;
    font-weight: 600;
    color: #8e8e93;
    cursor: pointer;
    border-bottom: 2px solid transparent;
    transition: color 0.15s, border-color 0.15s;
    display: inline-flex;
    align-items: center;
    gap: 8px;
    font-family: inherit;
}

.stk-tab:hover { color: #1c1c1e; }

.stk-tab--active {
    color: var(--panel-accent-text, #007AFF);
    border-bottom-color: var(--panel-accent-text, #007AFF);
}

.stk-tab-count {
    background: rgba(0, 0, 0, 0.06);
    color: #8e8e93;
    font-size: 10.5px;
    font-weight: 700;
    padding: 1px 7px;
    border-radius: 999px;
    min-width: 18px;
    text-align: center;
}

.stk-tab--active .stk-tab-count {
    background: rgba(var(--panel-accent-rgb, 0, 122, 255), 0.15);
    color: var(--panel-accent-text);
}

html.dark .stk-tabs,
html.midnight .stk-tabs { border-bottom-color: rgba(255, 255, 255, 0.08); }

html.cream .stk-tabs { border-bottom-color: rgba(61, 56, 50, 0.1); }

html.dark .stk-tab,
html.midnight .stk-tab { color: #9A9CAE; }
html.dark .stk-tab:hover,
html.midnight .stk-tab:hover { color: #EAE6E1; }

html.cream .stk-tab { color: #847C72; }
html.cream .stk-tab:hover { color: #3D3832; }

html.dark .stk-tab-count,
html.midnight .stk-tab-count {
    background: rgba(255, 255, 255, 0.08);
    color: #9A9CAE;
}

html.cream .stk-tab-count {
    background: rgba(61, 56, 50, 0.08);
    color: #847C72;
}


/* SECTION */
.stk-section {
    display: flex;
    flex-direction: column;
    gap: 14px;
}

.stk-section-head {
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: 12px;
}

.stk-section-title {
    font-size: 14px;
    font-weight: 700;
    color: #1c1c1e;
}

.stk-section-actions {
    display: flex;
    gap: 8px;
}

.stk-mvm-filter {
    display: flex;
    align-items: flex-end;
    gap: 12px;
    flex-wrap: wrap;
    padding: 14px 16px;
    border-radius: 12px;
    background: rgba(0, 0, 0, 0.025);
    border: 1px solid rgba(0, 0, 0, 0.06);
}

.stk-mvm-filter-field {
    display: flex;
    flex-direction: column;
    gap: 4px;
    min-width: 160px;
    flex: 1;
}

.stk-mvm-filter-label {
    font-size: 10.5px;
    font-weight: 600;
    color: #8e8e93;
    text-transform: uppercase;
    letter-spacing: 0.3px;
}

.stk-mvm-filter-reset {
    flex-shrink: 0;
}

html.dark .stk-mvm-filter,
html.midnight .stk-mvm-filter {
    background: rgba(255, 255, 255, 0.03);
    border-color: rgba(255, 255, 255, 0.06);
}

html.cream .stk-mvm-filter {
    background: rgba(61, 56, 50, 0.04);
    border-color: rgba(61, 56, 50, 0.08);
}

html.dark .stk-mvm-filter-label,
html.midnight .stk-mvm-filter-label { color: #9A9CAE; }

html.cream .stk-mvm-filter-label { color: #847C72; }

@media (max-width: 720px) {
    .stk-mvm-filter-field {
        min-width: calc(50% - 6px);
    }
}

html.dark .stk-section-title,
html.midnight .stk-section-title { color: #EAE6E1; }
html.cream .stk-section-title { color: #3D3832; }


/* Buttons */
.stk-btn {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    padding: 7px 13px;
    border-radius: 8px;
    border: 1px solid rgba(0, 0, 0, 0.08);
    background: rgba(0, 0, 0, 0.025);
    color: #1c1c1e;
    font-size: 12.5px;
    font-weight: 600;
    cursor: pointer;
    text-decoration: none;
    transition: background 0.15s, border-color 0.15s, transform 0.05s;
    font-family: inherit;
}

.stk-btn:hover { background: rgba(0, 0, 0, 0.04); }
.stk-btn:active { transform: scale(0.98); }

.stk-btn--sm { padding: 5px 10px; font-size: 12px; }

.stk-btn--ghost {
    background: transparent;
    border-color: rgba(0, 0, 0, 0.08);
}

.stk-btn--primary {
    background: var(--panel-accent, #007AFF);
    border-color: var(--panel-accent, #007AFF);
    color: #fff;
}

.stk-btn--primary:hover {
    background: var(--panel-accent, #007AFF);
    border-color: var(--panel-accent, #007AFF);
    color: #fff;
    filter: brightness(1.08);
}

.stk-btn:disabled,
.stk-btn[disabled] {
    opacity: 0.5;
    cursor: not-allowed;
}

html.dark .stk-btn,
html.midnight .stk-btn {
    background: rgba(255, 255, 255, 0.05);
    border-color: rgba(255, 255, 255, 0.08);
    color: #EAE6E1;
}

html.dark .stk-btn:hover,
html.midnight .stk-btn:hover { background: rgba(255, 255, 255, 0.08); }

html.dark .stk-btn--ghost,
html.midnight .stk-btn--ghost {
    background: transparent;
    border-color: rgba(255, 255, 255, 0.1);
}

html.cream .stk-btn {
    background: rgba(61, 56, 50, 0.04);
    border-color: rgba(61, 56, 50, 0.1);
    color: #3D3832;
}

html.cream .stk-btn:hover { background: rgba(61, 56, 50, 0.06); }


/* LOCATIONS GRID */
.stk-loc-grid {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(220px, 1fr));
    gap: 12px;
}

.stk-loc-card {
    padding: 14px 16px;
    border-radius: 12px;
    background: #fff;
    border: 1px solid rgba(0, 0, 0, 0.06);
    box-shadow: 0 1px 2px rgba(0, 0, 0, 0.03);
    display: flex;
    flex-direction: column;
    gap: 10px;
}

.stk-loc-head {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 8px;
}

.stk-loc-name {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    font-size: 13px;
    font-weight: 600;
    color: #1c1c1e;
    flex-wrap: wrap;
}

.stk-loc-default-pill {
    display: inline-flex;
    padding: 2px 7px;
    border-radius: 999px;
    background: rgba(var(--panel-accent-rgb, 0, 122, 255), 0.12);
    color: var(--panel-accent-text, #007AFF);
    font-size: 9.5px;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.3px;
}

.stk-loc-del {
    width: 26px;
    height: 26px;
    border: none;
    background: transparent;
    color: #c7c7cc;
    border-radius: 6px;
    cursor: pointer;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    transition: background 0.15s, color 0.15s;
}

.stk-loc-del:hover {
    background: rgba(239, 68, 68, 0.1);
    color: #ef4444;
}

.stk-loc-qty {
    display: flex;
    flex-direction: column;
    gap: 2px;
}

.stk-loc-qty-label {
    font-size: 10.5px;
    font-weight: 600;
    color: #8e8e93;
    text-transform: uppercase;
    letter-spacing: 0.3px;
}

.stk-loc-qty-value {
    font-size: 22px;
    font-weight: 700;
    color: #1c1c1e;
    font-variant-numeric: tabular-nums;
    line-height: 1.1;
}

.stk-loc-qty-value--zero { color: #c7c7cc; }

.stk-loc-actions {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 6px;
    margin-top: 4px;
}

.stk-loc-act {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 5px;
    padding: 6px 8px;
    border-radius: 7px;
    border: 1px solid rgba(0, 0, 0, 0.06);
    background: rgba(0, 0, 0, 0.025);
    color: #1c1c1e;
    font-size: 11.5px;
    font-weight: 600;
    cursor: pointer;
    transition: background 0.15s, color 0.15s, border-color 0.15s;
    font-family: inherit;
}

.stk-loc-act:hover {
    background: rgba(0, 0, 0, 0.04);
}

.stk-loc-act--in:hover {
    color: #28a745;
    border-color: rgba(40, 167, 69, 0.3);
    background: rgba(40, 167, 69, 0.08);
}

.stk-loc-act--out:hover {
    color: #ef4444;
    border-color: rgba(239, 68, 68, 0.3);
    background: rgba(239, 68, 68, 0.08);
}

html.dark .stk-loc-act,
html.midnight .stk-loc-act {
    background: rgba(255, 255, 255, 0.04);
    border-color: rgba(255, 255, 255, 0.07);
    color: #EAE6E1;
}

html.dark .stk-loc-act:hover,
html.midnight .stk-loc-act:hover {
    background: rgba(255, 255, 255, 0.07);
}

html.dark .stk-loc-act--in:hover,
html.midnight .stk-loc-act--in:hover { color: #34D399; }

html.dark .stk-loc-act--out:hover,
html.midnight .stk-loc-act--out:hover { color: #F87171; }

html.cream .stk-loc-act {
    background: rgba(61, 56, 50, 0.04);
    border-color: rgba(61, 56, 50, 0.08);
    color: #3D3832;
}

html.cream .stk-loc-act:hover {
    background: rgba(61, 56, 50, 0.06);
}

html.cream .stk-loc-act--in:hover { color: #059669; }

html.dark .stk-loc-card,
html.midnight .stk-loc-card {
    background: rgba(255, 255, 255, 0.025);
    border-color: rgba(255, 255, 255, 0.06);
    box-shadow: none;
}

html.cream .stk-loc-card {
    background: rgba(61, 56, 50, 0.03);
    border-color: rgba(61, 56, 50, 0.08);
}

html.dark .stk-loc-name,
html.midnight .stk-loc-name,
html.dark .stk-loc-qty-value,
html.midnight .stk-loc-qty-value { color: #EAE6E1; }

html.cream .stk-loc-name,
html.cream .stk-loc-qty-value { color: #3D3832; }

html.dark .stk-loc-qty-label,
html.midnight .stk-loc-qty-label { color: #9A9CAE; }

html.cream .stk-loc-qty-label { color: #847C72; }

html.dark .stk-loc-qty-value--zero,
html.midnight .stk-loc-qty-value--zero { color: #5a5b62; }


/* EMPTY */
.stk-empty {
    padding: 36px 20px;
    text-align: center;
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 8px;
    border: 1px dashed rgba(0, 0, 0, 0.1);
    border-radius: 12px;
    color: #8e8e93;
}

.stk-empty-title {
    font-size: 13px;
    color: #1c1c1e;
    font-weight: 600;
}

.stk-empty-hint {
    font-size: 11.5px;
}

html.dark .stk-empty,
html.midnight .stk-empty { border-color: rgba(255, 255, 255, 0.12); color: #9A9CAE; }

html.dark .stk-empty-title,
html.midnight .stk-empty-title { color: #EAE6E1; }

html.cream .stk-empty { border-color: rgba(61, 56, 50, 0.15); color: #847C72; }
html.cream .stk-empty-title { color: #3D3832; }


/* Movement table cells */
.stk-mvm-type {
    display: inline-flex;
    align-items: center;
    gap: 5px;
    padding: 3px 9px;
    border-radius: 999px;
    font-size: 11.5px;
    font-weight: 600;
}

.stk-mvm-type--in { background: rgba(40, 167, 69, 0.12); color: #28a745; }
.stk-mvm-type--out { background: rgba(239, 68, 68, 0.12); color: #ef4444; }
.stk-mvm-type--adj { background: rgba(217, 119, 6, 0.12); color: #d97706; }

html.dark .stk-mvm-type--in,
html.midnight .stk-mvm-type--in { color: #34D399; }
html.dark .stk-mvm-type--out,
html.midnight .stk-mvm-type--out { color: #F87171; }
html.dark .stk-mvm-type--adj,
html.midnight .stk-mvm-type--adj { color: #FBBF24; }

.stk-mvm-qty {
    font-weight: 700;
    font-variant-numeric: tabular-nums;
    font-size: 13.5px;
}

.stk-mvm-qty--in { color: #28a745; }
.stk-mvm-qty--out { color: #ef4444; }
.stk-mvm-qty--adj { color: #d97706; }

html.dark .stk-mvm-qty--in,
html.midnight .stk-mvm-qty--in { color: #34D399; }
html.dark .stk-mvm-qty--out,
html.midnight .stk-mvm-qty--out { color: #F87171; }
html.dark .stk-mvm-qty--adj,
html.midnight .stk-mvm-qty--adj { color: #FBBF24; }

.stk-mvm-loc { font-size: 12.5px; color: #1c1c1e; }
.stk-mvm-price {
    font-variant-numeric: tabular-nums;
    font-size: 12.5px;
    color: #1c1c1e;
    margin-right: 4px;
}
.stk-mvm-expense-pill {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 18px;
    height: 18px;
    border-radius: 5px;
    background: rgba(239, 68, 68, 0.12);
    color: #ef4444;
    margin-left: 4px;
    cursor: help;
}

.stk-mvm-note { font-size: 12.5px; color: #1c1c1e; }
.stk-mvm-source {
    font-size: 11.5px;
    color: var(--panel-accent-text, #007AFF);
    font-weight: 600;
}
.stk-mvm-muted { color: #c7c7cc; }

html.dark .stk-mvm-loc,
html.midnight .stk-mvm-loc,
html.dark .stk-mvm-price,
html.midnight .stk-mvm-price,
html.dark .stk-mvm-note,
html.midnight .stk-mvm-note { color: #EAE6E1; }

html.cream .stk-mvm-loc,
html.cream .stk-mvm-price,
html.cream .stk-mvm-note { color: #3D3832; }

html.dark .stk-mvm-expense-pill,
html.midnight .stk-mvm-expense-pill { color: #F87171; }

.stk-action--disabled {
    opacity: 0.4;
    cursor: not-allowed;
    pointer-events: none;
}
</style>
